/**
 * @deprecated 此文件已被废弃，请使用 myRequest.js
 * 
 * 所有新的代码都应直接导入 myRequest.js 中的实例，
 * 并使用 myXxxApi.js 中提供的API函数。
 * 
 * 例如：
 * // 老方式 (不推荐)
 * import request from '@/utils/request';
 * import { login } from '@/api/user';
 * 
 * // 新方式 (推荐)
 * import myRequest from '@/utils/myRequest';
 * import { myLogin } from '@/api/myUserApi';
 */

import axios from 'axios';
import { message } from 'ant-design-vue';
import router from '@/router';
import tokenService from '@/utils/tokenService';

// 说明：这是旧版本的请求实例，建议使用 myRequest.js 中的实例
// 为了兼容性暂时保留，未来版本可能会移除
console.log('【警告】使用了旧版本的request实例，建议使用myRequest');
console.warn('【废弃警告】request.js已被废弃，请迁移到myRequest.js');

// 创建axios实例
// 使用环境变量配置API基础URL，添加调试信息
const getApiBaseUrl = () => {
  const envUrl = import.meta.env.VITE_APP_API_BASE_URL;
  console.log('🔧 [API配置] 环境变量 VITE_APP_API_BASE_URL:', envUrl);
  
  // 如果环境变量明确设置为空字符串，使用相对路径（生产环境推荐）
  if (envUrl === '') {
    console.log('🔧 [API配置] 使用相对路径（当前域名）');
    return '';  // 返回空字符串，axios会使用相对路径
  }
  
  // 如果环境变量有值且不是'undefined'字符串，使用环境变量
  if (envUrl && envUrl !== 'undefined') {
    console.log('🔧 [API配置] 使用环境变量URL:', envUrl);
    return envUrl;
  }
  
  // 开发环境fallback：根据当前协议和端口判断环境
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  
  let fallbackUrl;
  if (isLocalhost) {
    fallbackUrl = 'http://localhost:8080';
  } else {
    // 生产环境使用相对路径（当前域名）
    fallbackUrl = '';
  }
  
  console.log('🔧 [API配置] 使用备用URL:', fallbackUrl || '相对路径（当前域名）');
  return fallbackUrl;
};

const myRequest = axios.create({
  baseURL: getApiBaseUrl(),
  timeout: import.meta.env.VITE_APP_API_TIMEOUT || 360000,
  withCredentials: true  // 允许请求携带cookie，关键用于session认证
});

// 请求拦截器
myRequest.interceptors.request.use(
  config => {
    console.log('【请求拦截器】开始发送请求:');
    console.log(`【请求拦截器】- URL: ${config.baseURL}${config.url}`);
    console.log(`【请求拦截器】- 方法: ${config.method.toUpperCase()}`);
    console.log('【请求拦截器】- 请求头:', config.headers);
    
    // 添加JWT认证头
    const accessToken = tokenService.getAccessToken();
    if (accessToken) {
      config.headers['Authorization'] = `Bearer ${accessToken}`;
      console.log('【请求拦截器】已添加JWT认证头');
    } else {
      console.log('【请求拦截器】未找到JWT token');
    }
    
    if (config.params) {
      console.log('【请求拦截器】- 查询参数:', config.params);
    }
    
    if (config.data) {
      if (config.data instanceof FormData) {
        console.log('【请求拦截器】- 请求体: [FormData对象]');
        // FormData对象不便于直接打印，在myBookApi.js中已经打印了其内容
      } else {
        console.log('【请求拦截器】- 请求体:', config.data);
      }
    }
    
    return config;
  },
  error => {
    console.error('【请求拦截器】请求发送失败:', error);
    return Promise.reject(error);
  }
);

// 响应拦截器
myRequest.interceptors.response.use(
  response => {
    // 调试信息
    console.log('【响应拦截器】收到响应:');
    console.log(`【响应拦截器】- URL: ${response.config.url}`);
    console.log(`【响应拦截器】- 状态码: ${response.status}`);
    console.log('【响应拦截器】- 响应数据:', response.data);
    
    // 从响应中获取数据
    const res = response.data;
    
    // 处理音乐上传接口的特殊情况
    if (response.config.url.includes('/api/music/upload')) {
      console.log('【响应拦截器】检测到音乐上传响应，进行特殊处理');
      
      // 检查响应是否包含音乐记录数据
      if (res.code === 0 && res.data && res.data.id) {
        console.log('【响应拦截器】音乐上传成功，返回音乐数据');
        return res.data;
      }
    }
    
    // 如果状态码不为0，说明后端接口返回了错误信息
    if (res.code !== 0) {
      console.warn(`【响应拦截器】响应状态码非0: code=${res.code}, message=${res.message}`);
      
      // 这里可能是个问题 - 如果后端返回的message包含"成功"字样，但code不为0
      // 可能是后端响应格式不一致造成的
      if (res.message && res.message.includes('成功')) {
        console.log('【响应拦截器】检测到可能的误判情况：消息包含"成功"但code不为0');
        // 尝试当作成功处理
        return res.data;
      }
      
      message.error(res.message || '请求出错');
      
      // 如果是未登录状态，跳转到登录页
      if (res.code === 40100) {
        console.log('【响应拦截器】用户未登录，准备跳转到登录页');
        localStorage.removeItem('isLoggedIn');
        localStorage.removeItem('userRole');
        localStorage.removeItem('username');
        
        router.push({
          path: '/login',
          query: { redirect: router.currentRoute.value.fullPath }
        });
      }
      
      return Promise.reject(new Error(res.message || '请求出错'));
    }
    
    console.log('【响应拦截器】响应状态码为0，请求成功');
    // 返回响应数据的data字段
    return res.data;
  },
  error => {
    // 如果是取消请求，不显示错误
    if (axios.isCancel(error)) {
      console.log('【响应拦截器】请求被取消');
      return Promise.reject(error);
    }
    
    console.error('【响应拦截器】请求失败:', error);
    
    // 详细记录错误信息
    if (error.response) {
      // 请求成功发出且服务器也响应了状态码，但状态代码超出了2xx的范围
      console.error(`【响应拦截器】服务器响应错误: 状态码=${error.response.status}`);
      console.error('【响应拦截器】错误响应数据:', error.response.data);
      console.error('【响应拦截器】错误响应头:', error.response.headers);
    } else if (error.request) {
      // 请求已经成功发起，但没有收到响应
      console.error('【响应拦截器】未收到响应:', error.request);
    } else {
      // 发送请求时出了点问题
      console.error('【响应拦截器】请求配置错误:', error.message);
    }
    
    // 特殊处理401错误（未授权/未登录）
    if (error.response && error.response.status === 401) {
      console.info('【响应拦截器】用户未登录或会话已过期');
      
      // 清除登录状态
      localStorage.removeItem('isLoggedIn');
      localStorage.removeItem('userRole');
      
      // 返回null而非抛出错误
      return Promise.resolve(null);
    }
    
    // 如果错误响应中包含成功的信息，尝试修复
    if (error.response && error.response.data) {
      const errData = error.response.data;
      
      if (errData.message && errData.message.includes('成功')) {
        console.log('【响应拦截器】检测到响应包含成功信息但被当作错误处理');
        message.success(errData.message || '操作成功');
        return errData.data;
      }
    }
    
    // 特殊处理：OneDrive存储类型检查的400错误不显示弹窗
    if (error.response && error.response.status === 400 && error.response.data) {
      const errData = error.response.data;
      if (errData.message && errData.message.includes('不是OneDrive存储')) {
        console.log('【响应拦截器】检测到OneDrive存储类型检查错误，不显示弹窗');
        return Promise.reject(error);
      }
    }
    
    // 🔥 检查是否是登录API，如果是则不显示网络错误提示
    const url = error.config?.url || '';
    const noErrorApis = [
      '/api/user/login',             // 登录API（登录失败时不显示网络错误）
      '/api/user/refresh-token',     // 刷新token API（token失效时不显示错误）
    ];
    
    // 🔥 对于登录API，完全跳过所有类型的网络错误提示
    if (noErrorApis.some(api => url.includes(api))) {
      console.log(`【响应拦截器】API ${url} 无需显示网络错误提示`);
      return Promise.reject(error);
    }
    
    // 🔥 使用异步调用避免阻塞UI渲染
    setTimeout(() => {
      message.error('网络错误，请稍后重试');
    }, 0);
    
    return Promise.reject(error);
  }
);

// 导出自定义请求实例
export default myRequest;
// 导出提醒函数
export function useRequestDeprecationWarning() {
  console.warn('请使用myRequest.js中的实例替代request.js');
}
// 如果需要原生 axios，可以这样导出
// export { axios as myRawAxios }; 